/* ==========================================================================
   cp-scroll-reveal.js                          OWNER: the fix pass, 2026-08-11
   Loaded from layout/theme.liquid immediately after animations.js, inside the
   same "settings.animations_reveal_on_scroll" guard, so it costs nothing when
   the reveal is switched off.

   WHAT THIS FILE IS FOR
   ---------------------
   A safety net under Refresh's own reveal on scroll, not a second reveal.
   Nothing here starts an animation, sets a style or touches markup: the one
   thing it does is take the class "scroll-trigger--offscreen" off an element
   that is inside the viewport, which is exactly what animations.js does when
   it is working. Refresh paints an unrevealed element at opacity 0.01
   (base.css, ".scroll-trigger.animate--slide-in"), so an element that keeps
   that class while the reader is looking at it is invisible content, not a
   missing animation. On this page that is the whole category row and the whole
   footer block set.

   THE HOLE IT PLUGS
   -----------------
   assets/animations.js reveals an element and then calls observer.unobserve on
   it in the same pass:

       if (element.isIntersecting) {
         ... remove the offscreen class ...
         observer.unobserve(elementTarget);
       } else {
         element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
       }

   An IntersectionObserver callback carries a batch of entries, and a jump
   scroll (End, a hash link, browser scroll restoration, a back button) can put
   more than one entry for the same element in one batch. If the last entry in
   that batch is a stale "not intersecting", the else branch puts the class back
   on an element that has already been unobserved, so no further callback can
   ever take it off again. The element is inside the viewport and permanently at
   opacity 0.01. Nothing in the theme recovers from that state, which is why
   slow scrolling looks fine and a jump does not.

   HOW THE NET WORKS
   -----------------
   1. Sweep. Walk every .scroll-trigger, and for any element inside the viewport
      take the offscreen class off and mark it revealed. The viewport test uses
      the same 50px bottom inset as the rootMargin in animations.js, so the net
      and the observer agree about where the fold is and the net can never
      reveal something a fraction early.
   2. Latch. Once an element is marked revealed a MutationObserver watches its
      class attribute and removes the offscreen class if anything puts it back.
      That is the race above, closed at the point it happens rather than at the
      next scroll. Refresh never legitimately re-hides a revealed element: it
      has stopped observing it by then.
   3. Sweep again on everything that can move the viewport without a normal
      scroll: first paint, full load, bfcache restore, hash change, history
      navigation, resize, the tab coming back to the front, and the end of any
      scroll. scrollend is used where the browser has it and a debounced scroll
      listener stands in where it does not, so Safari and older Chrome get the
      same guarantee.

   WHAT IT DELIBERATELY DOES NOT DO
   --------------------------------
   No styles, no inline attributes, no second IntersectionObserver, no reveal of
   anything that is off screen. An element below the fold keeps its class and
   still animates in the ordinary way, so the build's motion is unchanged: this
   only ever removes an invisible state from content the reader can already see.
   ========================================================================== */

(function () {
  'use strict';

  var TRIGGER_CLASS = 'scroll-trigger';
  var OFFSCREEN_CLASS = 'scroll-trigger--offscreen';
  var REVEALED_ATTR = 'data-cp-revealed';

  /* animations.js observes with rootMargin '0px 0px -50px 0px'. Matching it
     keeps the two in step: the net reveals an element at the same moment the
     observer would have. */
  var BOTTOM_INSET = 50;

  var latch = null;
  if (typeof MutationObserver === 'function') {
    latch = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var el = records[i].target;
        if (el.getAttribute(REVEALED_ATTR) === 'true' && el.classList.contains(OFFSCREEN_CLASS)) {
          el.classList.remove(OFFSCREEN_CLASS);
        }
      }
    });
  }

  function insideViewport(el) {
    var box = el.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) return false;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return box.top < viewportHeight - BOTTOM_INSET && box.bottom > 0;
  }

  function reveal(el) {
    el.classList.remove(OFFSCREEN_CLASS);
    if (el.getAttribute(REVEALED_ATTR) === 'true') return;
    el.setAttribute(REVEALED_ATTR, 'true');
    if (latch) latch.observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  function sweep() {
    /* Live HTMLCollection, so a section added by the theme editor is picked up
       by the next sweep without re-querying the document. */
    var elements = document.getElementsByClassName(TRIGGER_CLASS);
    for (var i = 0; i < elements.length; i++) {
      if (insideViewport(elements[i])) reveal(elements[i]);
    }
  }

  var queued = false;
  function sweepSoon() {
    if (queued) return;
    queued = true;
    /* A timer, not requestAnimationFrame. rAF is the obvious choice for
       anything that reads layout, and it is the wrong one here: the browser
       stops serving animation frames to a tab that is not being painted, so a
       page restored from bfcache into a background tab, or measured by any
       automated browser, would queue a sweep that never runs and then refuse
       every later sweep because one was already pending. Timers keep running
       in a hidden tab. getBoundingClientRect flushes layout itself, so the
       sweep is accurate whenever it lands. */
    window.setTimeout(function () {
      queued = false;
      sweep();
    }, 0);
  }

  sweepSoon();

  window.addEventListener('load', sweepSoon);
  window.addEventListener('pageshow', sweepSoon); /* bfcache restore */
  window.addEventListener('hashchange', sweepSoon);
  window.addEventListener('popstate', sweepSoon);
  window.addEventListener('resize', sweepSoon, { passive: true });
  window.addEventListener('orientationchange', sweepSoon);
  document.addEventListener('visibilitychange', sweepSoon);
  document.addEventListener('shopify:section:load', sweepSoon);

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', sweepSoon, { passive: true });
  } else {
    /* Stand-in for scrollend. 120ms of quiet is long enough that a smooth or
       momentum scroll is treated as one gesture, short enough that a reader who
       jumps and stops never sees a blank band. */
    var idle = null;
    window.addEventListener(
      'scroll',
      function () {
        if (idle) clearTimeout(idle);
        idle = setTimeout(sweepSoon, 120);
      },
      { passive: true }
    );
  }
})();
